// RegisterLandingPageForm.tsx
import React, { useState } from "react";
import "../style/css/base.css";
import "../style/css/FormForLandingPage.css";
import { useAuth } from "../AuthContext";

type FormValues = {
  full_name: string;
  email: string;
  phone: string;
};

interface Props {
  onFormSubmit?: () => void;
}

export function RegisterLandingPageForm({ onFormSubmit }: Props) {
  const { leads } = useAuth();

  const [values, setValues] = useState<FormValues>({
    full_name: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof FormValues, string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  function handleChange<K extends keyof FormValues>(key: K, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function showCustomAlert(message: string) {
    setAlertMessage(message);
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  }

  function validate(): boolean {
    const nextErrors: Partial<Record<keyof FormValues, string>> = {};

    if (!values.full_name) nextErrors.full_name = "El nombre es obligatorio.";

    if (!values.email) nextErrors.email = "El email es obligatorio.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      nextErrors.email = "Ingresa un email válido.";

    if (!values.phone) nextErrors.phone = "El teléfono es obligatorio.";
    else {
      // Remove any non-digit characters for validation
      const digitOnlyPhone = values.phone.replace(/\D/g, "");
      if (digitOnlyPhone.length < 10) {
        nextErrors.phone = "El teléfono debe tener al menos 10 dígitos.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  // RegisterLandingPageForm.tsx - Update submitToServer function
  async function submitToServer(payload: FormValues) {
    try {
      // Get referral code from URL if exists
      const urlParams = new URLSearchParams(window.location.search);
      const usedReferralCode = urlParams.get("ref") || "";

      const djangoPayload = {
        ...payload,
        practice_size: "solo",
        interest: "beta",
        profession: "Profesional de la salud",
        problem: "",
        used_referral_code: usedReferralCode, // Send the referral code if user came from a referral
      };

      const response = await leads(djangoPayload);

      // Now the response includes the referral_code generated by the backend
      const { referral_code } = response;

      // Save the referral code for displaying
      setReferralCode(referral_code);
      return true;
    } catch (err: any) {
      // Handle specific errors
      if (err.response?.data?.email) {
        showCustomAlert(err.response.data.email[0]);
      } else if (err.response?.data?.used_referral_code) {
        showCustomAlert(err.response.data.used_referral_code[0]);
      } else {
        showCustomAlert(
          "Ocurrió un error enviando los datos. Por favor, intenta nuevamente.",
        );
      }

      return false;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // Ensure phone number has +52 prefix if not already present
      let phoneNumber = values.phone.trim();
      if (!phoneNumber.startsWith("+52")) {
        // Remove any non-digit characters and add +52 prefix
        const digitsOnly = phoneNumber.replace(/\D/g, "");
        phoneNumber = "+52" + digitsOnly;
      }

      const payload = {
        ...values,
        phone: phoneNumber,
      };

      const success = await submitToServer(payload);
      if (success) {
        if (onFormSubmit) {
          onFormSubmit();
        }
        setShowCongratulations(true);
        showCustomAlert("¡Registro exitoso! Ahora invita a un amigo.");
      } else {
        showCustomAlert(
          "Ocurrió un error enviando los datos. Por favor, intenta nuevamente.",
        );
      }
    } catch (err: any) {
      showCustomAlert("Ocurrió un error. Por favor, intenta nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  }

  // Handle phone input with auto-formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters
    let digits = value.replace(/\D/g, "");

    // If user starts typing with 1, assume they want +521 (mobile)
    // Otherwise assume they're typing area code + number
    if (digits.length > 0) {
      // If it doesn't start with 52, add it
      if (!value.startsWith("+52")) {
        // Remove the +52 prefix if user typed it manually
        if (value.startsWith("+")) {
          // Keep whatever they typed after +
          value = "+" + digits;
        } else {
          // Auto-add +52 prefix
          value = "+52" + digits;
        }
      }
    }

    handleChange("phone", value);
  };

  // Si ya se registró exitosamente, mostramos el componente de felicitaciones
  if (showCongratulations) {
    return (
      <CongratulationsWithReferral
        referralCode={referralCode}
        name={values.full_name}
      />
    );
  }

  return (
    <div className="form-card" aria-live="polite">
      <h3 style={{ marginBottom: "1rem", textAlign: "center" }}>
        Únete a la lista de espera
      </h3>

      {/* Custom Alert */}
      {showAlert && (
        <div className="custom-alert custom-alert-error">
          <span className="custom-alert-message">{alertMessage}</span>
          <button
            className="custom-alert-close"
            onClick={() => setShowAlert(false)}
            aria-label="Cerrar alerta"
          >
            ×
          </button>
        </div>
      )}

      <form id="leadForm" onSubmit={handleSubmit} noValidate>
        {/* Nombre */}
        <div className="form-group">
          <label htmlFor="full_name">Nombre Completo *</label>
          <input
            id="full_name"
            name="full_name"
            type="text"
            placeholder="Dr. Juan Pérez"
            value={values.full_name}
            required
            onChange={(e) => handleChange("full_name", e.target.value)}
          />
          {errors.full_name && (
            <div style={{ color: "var(--error)", marginTop: 6, fontSize: 14 }}>
              {errors.full_name}
            </div>
          )}
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="tu@correo.com"
            value={values.email}
            required
            onChange={(e) => handleChange("email", e.target.value)}
          />
          {errors.email && (
            <div style={{ color: "var(--error)", marginTop: 6, fontSize: 14 }}>
              {errors.email}
            </div>
          )}
        </div>

        {/* Teléfono con +52 predefinido */}
        <div className="form-group">
          <label htmlFor="phone">WhatsApp *</label>
          <div className="phone-input-wrapper">
            <div className="phone-prefix">+52</div>
            <input
              id="phone"
              name="phone"
              type="tel"
              placeholder="55 1234 5678"
              value={values.phone.replace(/^\+52/, "")} // Remove
              required
              onChange={handlePhoneChange}
              className="phone-input"
            />
          </div>
          {errors.phone && (
            <div style={{ color: "var(--error)", marginTop: 6, fontSize: 14 }}>
              {errors.phone}
            </div>
          )}
          <p className="phone-note">Ejemplo: 55 1234 5678 (10 dígitos)</p>
        </div>

        {/* Acción */}
        <div className="action-footer" style={{ marginTop: 18 }}>
          <button
            type="submit"
            className="btn btn--primary btn--large btn--save"
            disabled={isSubmitting}
            aria-disabled={isSubmitting}
          >
            {isSubmitting ? "Enviando..." : "¡Quiero mi lugar en la Beta!"}
          </button>
        </div>

        <p
          style={{
            fontSize: "0.85rem",
            color: "var(--neutral-300)",
            textAlign: "center",
            marginTop: "1rem",
          }}
        >
          * Campos obligatorios
        </p>
      </form>
    </div>
  );
}

// Componente de Felicitaciones con Referido (Icons instead of emojis)
interface CongratulationsProps {
  referralCode: string;
  name: string;
}

export function CongratulationsWithReferral({
  referralCode,
  name,
}: CongratulationsProps) {
  const [copied, setCopied] = useState(false);

  const referralLink = `${window.location.origin}?ref=${referralCode}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {}
  };

  const handleShareWhatsApp = () => {
    const message = `¡Hola! Me acabo de unir a la lista de espera de Cara de Agenda, una herramienta increíble para automatizar la agenda de consultorios. 🩺\n\nSi te unes usando mi enlace, ambos obtenemos 1 mes GRATIS cuando lancemos.\n\n${referralLink}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
  };

  return (
    <div className="congratulations-card">
      <div className="congratulations-header">
        <div className="congratulations-icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="#198754"
              strokeWidth="2"
              fill="rgba(25, 135, 84, 0.1)"
            />
            <path
              d="M8 12l3 3 5-5"
              stroke="#198754"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3>¡Felicidades, {name.split(" ")[0]}!</h3>
        <p className="congratulations-subtitle">
          Estás en la lista de espera para la Beta de Cara de Agenda
        </p>
      </div>

      <div className="congratulations-content">
        {/* Bonus Program Explanation */}
        <div className="bonus-section">
          <div className="section-title-with-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 11l11-11L22 13l-11 11-11-11L2 0l11 11z"
                stroke="var(--color-accent)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 11l11-11L22 13l-11 11-11-11L2 0l11 11z"
                stroke="var(--color-accent)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h4>Gana meses GRATIS con nuestro programa de referidos</h4>
          </div>

          <div className="bonus-explanation">
            <div className="bonus-tier">
              <div className="bonus-tier-header">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                    stroke="#FFC107"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h5>Si eres seleccionado como Beta Tester</h5>
              </div>
              <p>
                <strong>6 MESES GRATIS</strong> +{" "}
                <strong>1 mes adicional</strong> por cada referido. Aseguramos
                el precio de fundador de $399/mes después del período gratuito.
              </p>
            </div>

            <div className="bonus-tier">
              <div className="bonus-tier-header">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
                    stroke="#0D6EFD"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6v12M18 12H6"
                    stroke="#0D6EFD"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h5>Si no estás entre los primeros 15</h5>
              </div>
              <p>
                <strong>1 mes GRATIS</strong> por cada referido +{" "}
                <strong>Precio de Fundador ($399/mes)</strong>. Tu referido
                también obtiene 1 mes gratis.
              </p>
            </div>
          </div>

          <div className="referral-section">
            <p className="referral-invitation">
              Comparte tu enlace personal para ganar meses GRATIS:
            </p>

            <div className="referral-code">
              <span className="code-label">Tu código de referido:</span>
              <span className="code-value">{referralCode}</span>
            </div>

            <div className="referral-link-container">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="referral-link-input"
              />
              <button
                onClick={handleCopyLink}
                className="btn btn--primary btn--copy"
              >
                {copied ? "¡Copiado!" : "Copiar"}
              </button>
            </div>

            <p className="referral-note">
              <strong>Por cada persona que se registre con tu enlace:</strong>
              <br />• Tú ganas <strong>1 mes GRATIS</strong>
              <br />• Tu referido gana <strong>1 mes GRATIS</strong>
            </p>
          </div>
        </div>

        <div className="next-steps">
          <div className="section-title-with-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                stroke="var(--color-accent)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
                stroke="var(--color-accent)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h4>¿Qué sigue?</h4>
          </div>
          <ul className="steps-list">
            <li>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                />
                <path
                  d="M8 12l3 3 5-5"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                Te enviaremos un correo de confirmación en las próximas 24 horas
              </span>
            </li>
            <li>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                />
                <path
                  d="M8 12l3 3 5-5"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                Seleccionaremos a los 15 Beta Testers y notificaremos por email
              </span>
            </li>
            <li>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                />
                <path
                  d="M8 12l3 3 5-5"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                Si eres seleccionado como Beta Tester:{" "}
                <strong>6 meses GRATIS</strong>
              </span>
            </li>
            <li>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                />
                <path
                  d="M8 12l3 3 5-5"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                Por cada referido: <strong>+1 mes GRATIS para ambos</strong>
              </span>
            </li>
            <li>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                />
                <path
                  d="M8 12l3 3 5-5"
                  stroke="#0D6EFD"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                Todos aseguran el <strong>precio de fundador: $399/mes</strong>
              </span>
            </li>
          </ul>
        </div>

        <div className="share-buttons">
          <button onClick={handleShareWhatsApp} className="btn btn--whatsapp">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Compartir por WhatsApp
          </button>

          <button
            onClick={() => window.location.reload()}
            className="btn btn--secondary"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
}

export default RegisterLandingPageForm;
